import React, { useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Plus, X, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import RealtorPeekModal from "@/components/realtors/RealtorPeekModal";

/**
 * RealtorPicker
 * -------------
 * Multi-select combobox that lets the user tag one or more realtors as
 * "Owner" / "Brokerage Contact" on a brokerage profile.
 *
 * - `options` is the pool of realtors (e.g. everyone at this brokerage).
 * - `value` is the ordered list of selected realtor IDs.
 * - `onChange(nextIds)` fires whenever the selection changes.
 * - Typing a name and pressing Enter auto-selects the first match; if there
 *   is no match, the input is ignored (no free-text values — the field is
 *   strictly a link to a realtor profile).
 * - Selected chips open the realtor's card as an in-place peek modal so
 *   the user doesn't lose their spot in the Brokerage details sheet.
 *
 * Mobile ergonomics: on iOS the CommandInput used to lose focus + reset
 * whenever the user dismissed the software keyboard, and the dropdown
 * could sit under the keyboard. Two mitigations:
 *   1) `query` state is NOT cleared when the popover closes — the user
 *      can dismiss the keyboard, tap a suggestion, and continue.
 *   2) On focus we scrollIntoView the trigger so the input sits near
 *      the top of the visual viewport, leaving room for the list to
 *      render above the keyboard.
 */
export default function RealtorPicker({
  value = [],
  onChange,
  options = [],
  placeholder = "Add a realtor…",
  emptyLabel = "No realtors at this brokerage yet.",
  testid = "realtor-picker",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [peekId, setPeekId] = useState(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);

  // Build a quick lookup so the selected chips can render the current name
  // even if the parent hasn't paginated the full options list.
  const byId = useMemo(() => {
    const m = new Map();
    for (const r of options) m.set(r.id, r);
    return m;
  }, [options]);

  const selected = useMemo(
    () => value.map((id) => byId.get(id)).filter(Boolean),
    [value, byId],
  );

  // Available = everyone in the pool not already selected.
  const available = useMemo(() => {
    const chosen = new Set(value);
    return options.filter((r) => !chosen.has(r.id));
  }, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return available;
    return available.filter((r) => {
      const name = r.name || `${r.first_name || ""} ${r.last_name || ""}`.trim();
      return name.toLowerCase().includes(q);
    });
  }, [available, query]);

  const add = (id) => {
    if (!id) return;
    if (value.includes(id)) return;
    onChange([...value, id]);
    // Clear the query only when the user commits a pick — that's the
    // moment where "the text served its purpose". Any other close path
    // (blur, tap-outside, keyboard dismiss) leaves the text alone.
    setQuery("");
  };

  const remove = (id) => onChange(value.filter((v) => v !== id));

  // Enter on the input auto-selects the first filtered match (if any).
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && filtered.length > 0) {
      e.preventDefault();
      add(filtered[0].id);
    }
  };

  // iOS keyboard rescue: when the input is focused, pull the trigger to
  // the top of the visible viewport with a 12px breathing room. Uses
  // scrollIntoView with a small timeout so it runs AFTER Safari has
  // opened the keyboard and adjusted the visualViewport height.
  const handleInputFocus = () => {
    const el = triggerRef.current;
    if (!el || typeof window === "undefined") return;
    if (!window.matchMedia || !window.matchMedia("(max-width: 767px)").matches) {
      return; // desktop / tablet — Radix handles it.
    }
    // Two-tick delay: first tick lets Popover animate open, second lets
    // Safari resize for the keyboard. 220ms works reliably across iOS 15+.
    window.setTimeout(() => {
      try {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch {
        // Older Safari versions with no smooth support — fall back to
        // instant scroll so the input at least becomes visible.
        el.scrollIntoView(true);
      }
    }, 220);
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((r) => {
            const name = r.name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || "Realtor";
            return (
              <span
                key={r.id}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white text-xs px-2 py-1"
                data-testid={`${testid}-chip-${r.id}`}
              >
                <button
                  type="button"
                  onClick={() => setPeekId(r.id)}
                  className="text-slate-900 hover:text-[hsl(var(--brand-strong))] font-medium truncate max-w-[180px] focus:outline-none focus-visible:underline"
                  title={`Open ${name}'s profile`}
                  data-testid={`${testid}-open-${r.id}`}
                >
                  {name}
                </button>
                <button
                  type="button"
                  onClick={() => remove(r.id)}
                  className="text-slate-400 hover:text-rose-600 shrink-0"
                  aria-label={`Remove ${name}`}
                  data-testid={`${testid}-remove-${r.id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      ) : null}

      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          // NOTE: we deliberately do NOT reset `query` here. Keeping the
          // typed text sticky is what lets the mobile user dismiss the
          // iOS keyboard, then tap a suggestion in the still-visible
          // dropdown without losing what they typed.
        }}
      >
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            variant="outline"
            size="sm"
            role="combobox"
            className="w-full justify-between text-slate-600 font-normal min-h-[36px]"
            data-testid={`${testid}-trigger`}
          >
            <span className="flex items-center gap-1.5 truncate">
              <Plus className="h-3.5 w-3.5" /> {placeholder}
            </span>
            <ChevronDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[--radix-popover-trigger-width] max-h-[min(60vh,var(--radix-popover-content-available-height))] overflow-hidden"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          data-testid={`${testid}-popover`}
        >
          <Command shouldFilter={false}>
            <div className="relative">
              <CommandInput
                ref={inputRef}
                value={query}
                onValueChange={setQuery}
                onKeyDown={handleKeyDown}
                onFocus={handleInputFocus}
                placeholder="Type a name…"
                data-testid={`${testid}-input`}
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    inputRef.current?.focus();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-700"
                  aria-label="Clear search"
                  data-testid={`${testid}-clear`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
            <CommandList className="max-h-[42vh]">
              <CommandEmpty>
                <div className="flex flex-col items-center gap-1 py-3 text-xs">
                  <Search className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-500">
                    {options.length === 0 ? emptyLabel : "No matches."}
                  </span>
                </div>
              </CommandEmpty>
              <CommandGroup>
                {filtered.slice(0, 30).map((r) => {
                  const name = r.name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || "Realtor";
                  return (
                    <CommandItem
                      key={r.id}
                      value={name}
                      onSelect={() => add(r.id)}
                      data-testid={`${testid}-option-${r.id}`}
                    >
                      <Check className="h-4 w-4 opacity-0 mr-2" />
                      <span className="truncate">{name}</span>
                      {r.title ? (
                        <span className="ml-2 text-xs text-slate-500 truncate">{r.title}</span>
                      ) : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {peekId ? (
        <RealtorPeekModal
          realtorId={peekId}
          onClose={() => setPeekId(null)}
        />
      ) : null}
    </div>
  );
}
